/**
 * Core registry module.
 *
 * Purpose: Maintains a registry mapping editorId → editor context.
 * Replaces the singleton pattern from legacy Plugin class with a proper registry.
 *
 * Key responsibilities:
 * - Register editor instances with their configuration
 * - Retrieve editor contexts by ID
 * - Unregister editors when they're destroyed
 * - Extract editor IDs from editor instances
 *
 * Dependencies: types/common.ts, core/state.ts, adapters/host/doc.ts
 */
import { getEditorId } from '@/adapters/host/doc';
import { clearState, createAnnotationState } from '@/core/state';
import { Console } from '@/utils/console';
/**
 * Registry mapping editorId -> EditorContext
 */
const registry = new Map();
/**
 * Registers an editor instance in the registry.
 * Creates initial state for the editor.
 *
 * @param editorId Unique identifier for the editor
 * @param editor The editor instance
 * @param config Extension configuration options
 * @returns EditorContext for the registered editor
 */
export const registerEditor = (editorId, editor, config) => {
    // Ensure editorId is set
    const finalEditorId = editorId || 'default';
    // Create initial state for this editor
    createAnnotationState(finalEditorId);
    const context = {
        editorId: finalEditorId,
        editor,
        config: config || {},
    };
    registry.set(finalEditorId, context);
    return context;
};
/**
 * Retrieves the editor context for a given editor ID.
 *
 * @param editorId Unique identifier for the editor
 * @returns EditorContext or null if not found
 */
export const getEditorContext = (editorId) => {
    const context = registry.get(editorId);
    return context || null;
};
/**
 * Unregisters an editor from the registry and cleans up its state.
 *
 * @param editorId Unique identifier for the editor
 */
export const unregisterEditor = (editorId) => {
    registry.delete(editorId);
    clearState(editorId);
};
/**
 * Extracts the editor ID from an editor instance.
 * Checks editor storage first, then DOM attributes.
 *
 * @param editor The editor instance
 * @returns Editor ID or null if not found
 */
export const getEditorIdFromEditor = (editor) => {
    try {
        // Use the host adapter to extract editor ID
        // This avoids importing editor types here
        return getEditorId(editor);
    }
    catch (error) {
        Console.catch('Error extracting editor ID:', error);
        return null;
    }
};
/**
 * Gets all registered editor IDs.
 *
 * @returns Array of editor IDs
 */
export const getAllEditorIds = () => {
    return Array.from(registry.keys());
};
